import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { isClient } from '../../utils/utils';
import { bffPut } from '../bff';
import { BffQueryKey } from '../model/BffQueryKey';
import { useBasket } from './useBasket';
import type { OrderAddress } from './model';
import type { BasketAvailability } from './model/BasketAvailability';

interface UpdateShippingAddressProps {
    address: OrderAddress;
    useAsBilling?: boolean;
}

export const useUpdateShippingAddress = (): UseMutationResult<
    BasketAvailability,
    unknown,
    UpdateShippingAddressProps
> => {
    const { data } = useBasket();
    const authContext = useAuthContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ address, useAsBilling = false }: UpdateShippingAddressProps) => {
            if (isClient) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();

            return bffPut(`/baskets/${data?.basketId}/shipping-address`, {
                basketCustomerId: data?.customerInfo?.customerId,
                accessToken: authData.access_token,
                customerId: authData.customer_id,
                body: address,
                httpParams: [
                    {
                        useAsBilling,
                    },
                ],
            });
        },
        onSuccess: async (availability): Promise<void> => {
            queryClient.setQueryData(
                [
                    BffQueryKey.BASKET_AVAILABILITY,
                    data?.basketId ?? '',
                    data?.c_bopisStoreID ?? '',
                    data?.c_goLocalStoreID ?? '',
                ],
                availability,
            );

            await Promise.all([
                queryClient.invalidateQueries([BffQueryKey.BASKET]),
                queryClient.invalidateQueries([BffQueryKey.SHIPPING_METHODS], {
                    exact: false,
                }),
                queryClient.invalidateQueries([BffQueryKey.CUSTOMER]),
            ]);
        },
    });
};
